import React, { useState, useEffect } from "react";
import {
  Card,
  Page,
  Layout,
  Stack,
  Loading,
  ProgressBar,
  Link,
  Banner,
  FooterHelp,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { ClockMinor } from "@shopify/polaris-icons";
import FeatureItem from "./FeatureItem";
import EnabledFeatureList from "./EnabledFeatureList";
import PlanShowSkeleton from "./SkeletonShow";
import { TitleBar } from "@shopify/app-bridge-react";
import { useGetPlan } from "../../../../hooks/queries/plans";
import { useLocation } from "react-router-dom";
import JSConfetti from "js-confetti";

export default function PlanShow(props) {
  const { t } = useTranslation();
  const [planDetails, setPlanDetails] = useState(null);

  const jsConfetti = new JSConfetti();
  const searchParams = new URLSearchParams(useLocation().search);
  const upgradeParam = searchParams.get("upgrade");
  const upgrade = upgradeParam === "true";

  const [showDowngradeBanner, setShowDowngradeBanner] = useState(
    upgradeParam && !upgrade
  );
  const [showThankyouBanner, setShowThankyouBanner] = useState(
    upgradeParam && upgrade
  );

  const { data, isError } = useGetPlan({
    onSuccess: () => {
      if (upgradeParam && upgrade) jsConfetti.addConfetti();
    },
  });
  useEffect(() => setPlanDetails(data), [data]);

  if (isError) return <p>{t("settings.plan.error")}</p>;
  if (!planDetails) return <PlanShowSkeleton />;

  const { announcement } = planDetails;
  const {
    name,
    description,
    price,
    max_locations,
    trial,
    charge,
    enabled_features,
  } = planDetails.plan;

  const downgradeBannerMarkup = showDowngradeBanner ? (
    <Layout.Section>
      <Banner
        title={t("settings.plan.banners.planUpdated")}
        onDismiss={() => setShowDowngradeBanner(false)}
      >
        <p>
          <Link
            onClick={() => {
              window.Beacon("open");
              window.Beacon("prefill", {
                text: "",
              });
              window.Beacon("navigate", "/ask/");
            }}
          >
            {t("settings.plan.banners.letUsKnow")}
          </Link>{" "}
          {t("settings.plan.banners.improvementText")}
        </p>
      </Banner>
    </Layout.Section>
  ) : null;

  const thankyouBannerMarkup = showThankyouBanner ? (
    <Layout.Section>
      <Banner
        title={t("settings.plan.banners.thankYouUpgrading")}
        status="success"
        onDismiss={() => setShowThankyouBanner(false)}
      >
        <p>
          <Link
            onClick={() => {
              window.Beacon("open");
              window.Beacon("prefill", {
                text: "",
              });
              window.Beacon("navigate", "/ask/");
            }}
          >
            {t("settings.plan.banners.letUsKnow")}
          </Link>{" "}
          {t("settings.plan.banners.improvementText")}
        </p>
      </Banner>
    </Layout.Section>
  ) : null;

  return (
    <Page>
      <TitleBar title={t("settings.plan.title")} secondaryActions={[]} />
      <Layout>
        {announcement && (
          <Layout.Section fullWidth>
            <Banner title={announcement.title} status={announcement.status}>
              <p dangerouslySetInnerHTML={{ __html: announcement.text }}></p>
            </Banner>
          </Layout.Section>
        )}
        {downgradeBannerMarkup}
        {thankyouBannerMarkup}
        <Layout.Section>
          <Card
            primaryFooterAction={{
              content: t("settings.plan.changePlan"),
              url: "/settings/plan/edit",
            }}
          >
            <Card.Section>
              <Stack alignment="trailing">
                <Stack.Item fill>
                  <Stack vertical spacing="tight">
                    <Text variant="headingMd" as="h2">{name}</Text>
                    <Text  variant="bodyMd" as="span" color="subdued">{description}</Text>
                  </Stack>
                </Stack.Item>
                {price && (
                  <Stack spacing="tight" alignment="trailing">
                    <Text variant="heading4xl" as="p">${price}</Text>
                    <Text variant="bodyMd" as="span">{t("settings.plan.usdPerMonth")}</Text>
                  </Stack>
                )}
              </Stack>
            </Card.Section>
            <Card.Section title={t("settings.plan.sections.yourPlanIncludes")}>
              <EnabledFeatureList
                enabledFeatures={enabled_features}
                spacing="tight"
                showDescriptions={false}
              />

              <div style={{ marginTop: "1rem" }}>
                {max_locations ? (
                  <Stack vertical spacing="tight">
                    <Stack distribution="equalSpacing">
                      <FeatureItem
                        title={
                          <Text variant="bodyMd" as="span">{t("settings.plan.locations.upTo")}{" "}
                            <Text  variant="bodyMd" as="span" fontWeight="semibold">
                              {max_locations} {t("settings.plan.locations.locations")}</Text>
                          </Text>
                        }
                      />
                      <Stack.Item>
                        <Text  variant="bodyMd" as="span" color="subdued">
                          {t("settings.plan.locations.ofUsed", { used: planDetails.locations_used, max: max_locations })}
                        </Text>
                      </Stack.Item>
                    </Stack>
                    <ProgressBar
                      progress={
                        (planDetails.locations_used / max_locations) * 100
                      }
                    />
                  </Stack>
                ) : (
                  <FeatureItem
                    title={
                      <Text  variant="bodyMd" as="span" fontWeight="semibold">{t("settings.plan.locations.unlimited")}</Text>
                    }
                    description={`${planDetails.locations_used} ${t("settings.plan.locations.used")}`}
                  />
                )}
              </div>
            </Card.Section>
            {charge && (
              <Card.Section title={t("settings.plan.sections.nextBillingDate")}>
                <p>{charge.next_billing_description}</p>
              </Card.Section>
            )}
            {trial && (
              <Card.Section title={t("settings.plan.sections.trial")}>
                <Stack vertical spacing="tight">
                  <Stack distribution="equalSpacing">
                    <Stack.Item>
                      <FeatureItem
                        icon={ClockMinor}
                        title={t("settings.plan.trial.daysLeft", { days: trial.days_left })}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Text  variant="bodyMd" as="span" color="subdued">
                        {t("settings.plan.trial.daysUsed", { used: trial.days_used, total: trial.days_total })}
                      </Text>
                    </Stack.Item>
                  </Stack>
                  <ProgressBar
                    progress={
                      trial.days_used > 0
                        ? (trial.days_used / trial.days_total) * 100
                        : 0
                    }
                  />
                </Stack>
              </Card.Section>
            )}
          </Card>
        </Layout.Section>
      </Layout>
      <FooterHelp>
        <Text variant="bodyMd" as="span">{t("settings.plan.footer.description")}{" "}
          <Link url="https://www.sosuper.app/terms/">{t("settings.plan.footer.termsOfService")}</Link>{" "}
          {t("settings.plan.footer.and")}{" "}
          <Link url="https://www.sosuper.app/privacy/">{t("settings.plan.footer.privacyPolicy")}</Link>
        </Text>
      </FooterHelp>
    </Page>
  );
}
